const selectors = {
  content: '.group-block, [data-menu-content-node]',
  contentSource: '[data-menu-content-source]',
  grid: '[data-menu-grid-id]',
  menuItem: '.menu-list__list-item[data-menu-title]',
  panel: '.mega-menu__content-block',
  rail: '.mega-menu__custom-content',
  submenuLink: '[data-menu-submenu-title]',
  subtree: '[data-menu-tree-subtarget]',
  topLevelLink: '.menu-list__link',
  tree: '[data-menu-tree-target]',
};

const menuStructureSelector = `header-menu, ${selectors.grid}, ${selectors.contentSource}, ${selectors.rail}`;

let routingQueued = false;
let recoveryQueued = false;
const pendingHeightSyncs = new WeakSet();
const deferredPanelContent = new WeakMap();

/**
 * @typedef {Object} RoutedContent
 * @property {string} id
 * @property {number} minHeight
 * @property {HTMLElement} node
 * @property {string} subtarget
 * @property {number | 'fit-content'} width
 */

function queueMenuContentRouting() {
  if (routingQueued) return;

  routingQueued = true;
  requestAnimationFrame(() => {
    routingQueued = false;

    for (const source of document.querySelectorAll(selectors.contentSource)) {
      if (source instanceof HTMLTemplateElement) routeMenuContent(source);
    }
  });
}

/**
 * Flatten the configured hierarchy into content rails beside the native menu
 * links. Source order is retained within every top-level menu item.
 * @param {HTMLTemplateElement} source
 */
function routeMenuContent(source) {
  const menu = source.previousElementSibling;
  if (!(menu instanceof HTMLElement)) return;

  const menuItems = getMenuItems(menu);
  const contentByGrid = collectMenuContent(source, menuItems);
  const activeTargetsByGrid = removeExistingRails(menu);

  setupMenuContentSwitching(menu);

  for (const [grid, routedContent] of contentByGrid) {
    const rail = createContentRail(routedContent);
    grid.append(rail);

    const activeLink = findActiveSubmenuLink(grid);
    const activeTargets = activeTargetsByGrid.get(grid) ?? getSubmenuTargets(activeLink);
    showSubmenuContent(grid, activeTargets, { mount: isMenuGridActive(grid), syncHeight: false });
  }
}

/**
 * A hydration/editor rerender can replace a rail while its dropdown is open.
 * Mount that one immediately because a stationary pointer will not emit a new
 * pointerover event; closed grids remain lightweight shells.
 * @param {HTMLElement} grid
 * @returns {boolean}
 */
function isMenuGridActive(grid) {
  const menuItem = grid.closest(selectors.menuItem);
  if (!(menuItem instanceof HTMLElement)) return false;

  return menuItem.matches(':hover, :focus-within') || Boolean(menuItem.querySelector('[aria-expanded="true"]'));
}

/**
 * @param {HTMLElement} menu
 * @returns {HTMLElement[]}
 */
function getMenuItems(menu) {
  return Array.from(menu.querySelectorAll(selectors.menuItem)).filter((item) => item instanceof HTMLElement);
}

/**
 * Preserve the active submenu while replacing old routed content.
 * @param {HTMLElement} menu
 * @returns {Map<HTMLElement, string[]>}
 */
function removeExistingRails(menu) {
  const activeTargetsByGrid = new Map();

  for (const rail of menu.querySelectorAll(selectors.rail)) {
    if (!(rail instanceof HTMLElement)) continue;

    const grid = rail.parentElement;
    const activeTargets = rail.dataset.menuContentActiveSubtargets?.split(',').filter(Boolean) ?? [];

    if (grid instanceof HTMLElement && activeTargets.length > 0) {
      activeTargetsByGrid.set(grid, activeTargets);
    }

    rail.remove();
  }

  return activeTargetsByGrid;
}

/**
 * @param {RoutedContent[]} routedContent
 * @returns {HTMLElement}
 */
function createContentRail(routedContent) {
  const rail = document.createElement('div');
  const fragment = document.createDocumentFragment();
  const minHeight = Math.max(0, ...routedContent.map(({ minHeight: value }) => value));

  rail.className = 'mega-menu__custom-content';
  if (minHeight > 0) rail.style.setProperty('--menu-tree-min-height', `${minHeight}px`);

  for (const content of routedContent) {
    fragment.append(createDeferredContentPanel(content));
  }

  rail.append(fragment);
  return rail;
}

/**
 * Keep configured menu content inert until its menu item is opened. The source
 * already lives in a <template>; cloning every panel during page startup would
 * connect every product card and start loading hundreds of hidden images.
 * @param {RoutedContent} content
 * @returns {HTMLElement}
 */
function createDeferredContentPanel(content) {
  const panel = document.createElement('div');

  configureContentPanel(panel, content);
  panel.dataset.menuContentDeferred = 'true';
  deferredPanelContent.set(panel, content);

  return panel;
}

/**
 * @param {RoutedContent} content
 * @returns {HTMLElement}
 */
function cloneContentPanel({ id, node, subtarget, width }) {
  const panel = /** @type {HTMLElement} */ (node.cloneNode(true));

  configureContentPanel(panel, { id, node, subtarget, width });
  return panel;
}

/**
 * @param {HTMLElement} panel
 * @param {RoutedContent} content
 */
function configureContentPanel(panel, { id, subtarget, width }) {
  panel.classList.add('mega-menu__content-block');
  if (id) panel.dataset.menuRoutedId = id;
  panel.dataset.menuContentWidth = String(width);
  panel.dataset.menuContentSubtarget = subtarget;
  panel.hidden = Boolean(subtarget);

  if (width !== 'fit-content') panel.style.setProperty('--menu-content-width', String(width));
}

/**
 * Replace a lightweight panel shell with its configured content on first use.
 * @param {HTMLElement} panel
 * @returns {HTMLElement}
 */
function mountContentPanel(panel) {
  const content = deferredPanelContent.get(panel);
  if (!content) return panel;

  const mountedPanel = cloneContentPanel(content);
  mountedPanel.hidden = panel.hidden;
  panel.replaceWith(mountedPanel);
  deferredPanelContent.delete(panel);

  return mountedPanel;
}

/**
 * @param {HTMLTemplateElement} source
 * @param {HTMLElement[]} menuItems
 * @returns {Map<HTMLElement, RoutedContent[]>}
 */
function collectMenuContent(source, menuItems) {
  const contentByGrid = new Map();

  for (const sourceNode of source.content.children) {
    if (!(sourceNode instanceof HTMLElement)) continue;

    const tree = findSelfOrDirectChild(sourceNode, selectors.tree);
    if (!(tree instanceof HTMLElement)) continue;

    const target = normalizeMenuItem(tree.dataset.menuTreeTarget);
    const grid = findTargetGrid(menuItems, target);
    if (!target || !grid) continue;

    collectTreeContent(tree, grid, contentByGrid);
  }

  return contentByGrid;
}

/**
 * @param {HTMLElement} tree
 * @param {HTMLElement} grid
 * @param {Map<HTMLElement, RoutedContent[]>} contentByGrid
 */
function collectTreeContent(tree, grid, contentByGrid) {
  const minHeight = clampNumber(tree.dataset.menuTreeMinHeight, 0, 1000, 0);

  for (const child of tree.children) {
    if (!(child instanceof HTMLElement)) continue;

    const subtree = findSelfOrDirectChild(child, selectors.subtree);
    if (subtree instanceof HTMLElement) {
      for (const contentNode of subtree.children) {
        if (contentNode instanceof HTMLElement) {
          addTreeContent(contentByGrid, grid, contentNode, subtree.dataset.menuTreeSubtarget, minHeight);
        }
      }
      continue;
    }

    addTreeContent(contentByGrid, grid, child, '', minHeight);
  }
}

/**
 * @param {Map<HTMLElement, RoutedContent[]>} contentByGrid
 * @param {HTMLElement} grid
 * @param {HTMLElement} sourceNode
 * @param {string | undefined} subtarget
 * @param {number} minHeight
 */
function addTreeContent(contentByGrid, grid, sourceNode, subtarget, minHeight) {
  const content = findSelfOrDirectChild(sourceNode, selectors.content);
  if (!(content instanceof HTMLElement)) return;

  const width = resolveContentWidth(content);
  if (width === 0) return;

  const routedContent = contentByGrid.get(grid) ?? [];
  routedContent.push({
    id: content.dataset.menuContentId || sourceNode.id || content.id,
    minHeight,
    node: sourceNode,
    subtarget: normalizeMenuItem(subtarget),
    width,
  });
  contentByGrid.set(grid, routedContent);
}

/**
 * Find a node itself or one of its immediate children without crossing into a
 * nested Group's own block tree.
 * @param {HTMLElement} node
 * @param {string} selector
 * @returns {HTMLElement | null}
 */
function findSelfOrDirectChild(node, selector) {
  if (node.matches(selector)) return node;
  return Array.from(node.children).find((child) => child.matches(selector)) ?? null;
}

/**
 * Add one delegated switcher per hydrated header menu.
 * @param {HTMLElement} menu
 */
function setupMenuContentSwitching(menu) {
  if (menu.dataset.menuContentSwitching === 'true') return;

  menu.dataset.menuContentSwitching = 'true';
  menu.addEventListener('pointerover', handleMenuContentActivation);
  menu.addEventListener('focusin', handleMenuContentActivation);
  menu.addEventListener('pointerout', handleMenuItemExit);
  menu.addEventListener('focusout', handleMenuItemExit);
}

/**
 * @param {PointerEvent | FocusEvent} event
 */
function handleMenuContentActivation(event) {
  if (!(event.target instanceof Element)) return;

  const submenuLink = event.target.closest(selectors.submenuLink);
  if (submenuLink instanceof HTMLElement) {
    const grid = submenuLink.closest(selectors.grid);
    if (grid instanceof HTMLElement) {
      showSubmenuContent(grid, getSubmenuTargets(submenuLink), { preserveOnMiss: true });
    }
    return;
  }

  let topLevelLink = event.target.closest(selectors.topLevelLink);
  let menuItem = topLevelLink?.closest(selectors.menuItem);

  // The top-level item has a CSS pseudo-element extending its hit area below
  // the text link. Pointer events from that area target the <li> itself.
  if (!topLevelLink && event.target.matches(selectors.menuItem)) {
    menuItem = event.target;
    topLevelLink = Array.from(menuItem.children).find((child) => child.matches(selectors.topLevelLink));
  }

  if (!(topLevelLink instanceof HTMLElement) || !(menuItem instanceof HTMLElement)) return;
  if (topLevelLink.parentElement !== menuItem) return;

  const grid = menuItem.querySelector(selectors.grid);
  if (grid instanceof HTMLElement) showSubmenuContent(grid, []);
}

/**
 * Reset to default only after the pointer or focus leaves the entire top-level
 * item. Moving between its links and content keeps the active panel stable.
 * @param {PointerEvent | FocusEvent} event
 */
function handleMenuItemExit(event) {
  if (!(event.target instanceof Element)) return;

  const menuItem = event.target.closest(selectors.menuItem);
  if (!(menuItem instanceof HTMLElement)) return;
  if (event.relatedTarget instanceof Node && menuItem.contains(event.relatedTarget)) return;

  const grid = menuItem.querySelector(selectors.grid);
  if (grid instanceof HTMLElement) showSubmenuContent(grid, [], { syncHeight: false });
}

/**
 * Show every block associated with the active submenu title or handle.
 * @param {HTMLElement} grid
 * @param {string[]} requestedTargets
 * @param {{mount?: boolean, preserveOnMiss?: boolean, syncHeight?: boolean}} [options]
 */
function showSubmenuContent(grid, requestedTargets, options = {}) {
  const { mount = true, preserveOnMiss = false, syncHeight = true } = options;
  const rail = getContentRail(grid);
  if (!rail) return;

  const panels = getContentPanels(rail);
  const requested = [...new Set(requestedTargets.filter(Boolean))];
  const availableTargets = new Set(panels.map((panel) => panel.dataset.menuContentSubtarget ?? ''));
  const matchingTargets = requested.filter((target) => availableTargets.has(target));

  if (preserveOnMiss && requested.length > 0 && matchingTargets.length === 0) return;

  const nextActiveTargets = matchingTargets.join(',');
  const currentActiveTargets = rail.dataset.menuContentActiveSubtargets ?? '';

  if (nextActiveTargets !== currentActiveTargets) {
    for (const panel of panels) {
      const panelTarget = panel.dataset.menuContentSubtarget ?? '';
      panel.hidden = matchingTargets.length > 0 ? !matchingTargets.includes(panelTarget) : Boolean(panelTarget);
    }

    if (nextActiveTargets) {
      rail.dataset.menuContentActiveSubtargets = nextActiveTargets;
    } else {
      delete rail.dataset.menuContentActiveSubtargets;
    }
  }

  if (mount) {
    for (const panel of getContentPanels(rail)) {
      if (!panel.hidden) mountContentPanel(panel);
    }
  }

  if (syncHeight) queueSubmenuHeightSync(grid);
}

/**
 * @param {Element | null | undefined} link
 * @returns {string[]}
 */
function getSubmenuTargets(link) {
  if (!(link instanceof HTMLElement)) return [];

  return [link.dataset.menuSubmenuTitle, link.dataset.menuSubmenuHandle]
    .map(normalizeMenuItem)
    .filter(Boolean);
}

/**
 * Preserve the active switch after an editor rerender under a stationary
 * pointer or keyboard focus.
 * @param {HTMLElement} grid
 * @returns {HTMLElement | null}
 */
function findActiveSubmenuLink(grid) {
  const hoveredLink = grid.querySelector(`${selectors.submenuLink}:hover`);
  if (hoveredLink instanceof HTMLElement) return hoveredLink;

  const focusedLink = document.activeElement instanceof Element ? document.activeElement.closest(selectors.submenuLink) : null;
  return focusedLink instanceof HTMLElement && grid.contains(focusedLink) ? focusedLink : null;
}

/**
 * @param {HTMLElement} grid
 * @returns {HTMLElement | null}
 */
function getContentRail(grid) {
  return Array.from(grid.children).find((child) => child.matches(selectors.rail)) ?? null;
}

/**
 * @param {HTMLElement} rail
 * @returns {HTMLElement[]}
 */
function getContentPanels(rail) {
  return Array.from(rail.children).filter(
    (child) => child instanceof HTMLElement && child.matches(selectors.panel)
  );
}

function queueSubmenuHeightSync(grid) {
  if (pendingHeightSyncs.has(grid)) return;

  pendingHeightSyncs.add(grid);
  requestAnimationFrame(() => {
    pendingHeightSyncs.delete(grid);
    syncSubmenuHeight(grid);
  });
}

/**
 * HeaderMenu measures a submenu when it first opens. Refresh those same CSS
 * variables when switched content changes the open dropdown height.
 * @param {HTMLElement} grid
 */
function syncSubmenuHeight(grid) {
  const submenu = grid.closest('.menu-list__submenu');
  const header = grid.closest('header-component');
  if (!(submenu instanceof HTMLElement) || !(header instanceof HTMLElement)) return;

  let submenuHeight = submenu.offsetHeight;
  const menuItem = grid.closest('.menu-list__list-item');

  if (menuItem instanceof HTMLElement && menuItem.slot === 'overflow') {
    const overflowHost = grid
      .closest('header-menu')
      ?.querySelector('[data-testid="header-menu-overflow-list"]');
    const overflow = overflowHost?.shadowRoot?.querySelector('[part="overflow"]');
    if (overflow instanceof HTMLElement) submenuHeight = overflow.offsetHeight;
  }

  if (submenuHeight <= 0) return;

  const overlapBoundary = header.hasAttribute('data-submenu-overlap-bottom-row')
    ? header.querySelector('.header__row--top')
    : header;
  const headerVisibleHeight = overlapBoundary instanceof HTMLElement ? overlapBoundary.offsetHeight : header.offsetHeight;

  header.style.setProperty('--submenu-height', `${submenuHeight}px`);
  header.style.setProperty('--full-open-header-height', `${submenuHeight + headerVisibleHeight}px`);
}

function startMenuContentRouting() {
  queueMenuContentRouting();
  rerouteAfterHeaderHydration();
  recoverContentAfterHeaderMutation();
  document.addEventListener('shopify:section:load', queueMenuContentRouting);
}

/**
 * The theme rebuilds deferred header navigation after hydration. Re-route after
 * that pass so the freshly rendered grids receive their content rails.
 */
function rerouteAfterHeaderHydration() {
  for (const source of document.querySelectorAll(selectors.contentSource)) {
    const section = source.closest('.shopify-section');
    if (!section || section.dataset.hydrated === 'true') continue;

    const observer = new MutationObserver(() => {
      if (section.dataset.hydrated !== 'true') return;

      observer.disconnect();
      queueMenuContentRouting();
    });

    observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
  }
}

/**
 * Header internals can be replaced outside Shopify's section events. Observe
 * only structural mutations and restore a rail when configured content is lost.
 */
function recoverContentAfterHeaderMutation() {
  const headerGroup = document.querySelector('#header-group');
  if (!(headerGroup instanceof HTMLElement)) return;

  const observer = new MutationObserver((mutations) => {
    if (mutations.some(mutationChangesMenuStructure)) queueMissingContentRecovery();
  });
  observer.observe(headerGroup, { childList: true, subtree: true });
}

/**
 * Ignore DOM work inside product cards and other routed content.
 * @param {MutationRecord} mutation
 */
function mutationChangesMenuStructure(mutation) {
  const target = mutation.target;
  if (target instanceof Element && target.matches('#header-group, header-menu, [data-menu-grid-id]')) return true;

  return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
    if (!(node instanceof Element)) return false;
    return node.matches(menuStructureSelector) || Boolean(node.querySelector(menuStructureSelector));
  });
}

function queueMissingContentRecovery() {
  if (recoveryQueued) return;

  recoveryQueued = true;
  requestAnimationFrame(() => {
    recoveryQueued = false;

    for (const source of document.querySelectorAll(selectors.contentSource)) {
      if (source instanceof HTMLTemplateElement) recoverMissingContent(source);
    }
  });
}

/**
 * @param {HTMLTemplateElement} source
 */
function recoverMissingContent(source) {
  const menu = source.previousElementSibling;
  if (!(menu instanceof HTMLElement)) return;

  const contentByGrid = collectMenuContent(source, getMenuItems(menu));
  const hasUnexpectedRail = Array.from(menu.querySelectorAll(selectors.rail)).some((rail) => {
    return !(rail.parentElement instanceof HTMLElement) || !contentByGrid.has(rail.parentElement);
  });
  const hasMissingContent = Array.from(contentByGrid).some(([grid, expectedContent]) => {
    const rail = getContentRail(grid);
    if (!rail) return expectedContent.length > 0;

    const panels = getContentPanels(rail);
    if (panels.length !== expectedContent.length) return true;

    const routedIds = new Set(panels.map((panel) => panel.dataset.menuRoutedId).filter(Boolean));
    return expectedContent.some(({ id }) => id && !routedIds.has(id));
  });

  if (hasUnexpectedRail || hasMissingContent) routeMenuContent(source);
}

/**
 * @param {HTMLElement[]} menuItems
 * @param {string} target
 * @returns {HTMLElement | null}
 */
function findTargetGrid(menuItems, target) {
  const menuItem = menuItems.find((item) => {
    return [item.dataset.menuTitle, item.dataset.menuHandle].some((value) => normalizeMenuItem(value) === target);
  });
  const grid = menuItem?.querySelector(selectors.grid);

  return grid instanceof HTMLElement ? grid : null;
}

/**
 * Reuse a native Group block's width when a Menu products block has no explicit
 * percentage setting of its own.
 * @param {HTMLElement} content
 * @returns {number | 'fit-content'}
 */
function resolveContentWidth(content) {
  const explicitWidth = content.dataset.menuContentWidth;
  if (explicitWidth !== undefined) return clampNumber(explicitWidth, 0, 100, 100);

  const groupWidth = content.style.getPropertyValue('--size-style-width').trim();
  if (groupWidth === 'fit-content') return 'fit-content';
  if (groupWidth.endsWith('%')) return clampNumber(groupWidth, 0, 100, 100);

  return 100;
}

/**
 * @param {string | number | null | undefined} value
 * @param {number} min
 * @param {number} max
 * @param {number} fallback
 */
function clampNumber(value, min, max, fallback) {
  const number = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
}

/**
 * Normalize a menu title or handle for matching translated navigation labels to
 * language-independent editor settings.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeMenuItem(value) {
  return (value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startMenuContentRouting, { once: true });
} else {
  startMenuContentRouting();
}
